import { useMemo, useState } from 'react';

import { useRouteLoaderData } from '@remix-run/react';
import type { Price, PricingFormMode, Tier, TPricingAttributes } from '@shared/dream-components';
import {
  computeGroupTotalCents,
  DEFAULT_GROUP_MIN_QUANTITY,
  PRICING_ELEMENT_DEFAULT_INPUT_BORDER_RADIUS,
} from '@shared/dream-components';
import type { StripeElementsOptions } from '@stripe/stripe-js';

import useEmail from '~/hooks/useEmail';
import useStripeSession from '~/hooks/useStripeSession';
import { PriceInterval } from '~/interfaces/subscriptions';
import type { loader } from '~/root';

import { UpgradeContext } from './UpgradeContext';
import { useResolvedTierSelection } from './useResolvedTierSelection';

interface UpgradeProviderProps {
  children: React.ReactNode;
  tiers: Tier[];
  giftTiers: Tier[];
  groupTiers: Tier[];
  isInternalCheckout: boolean;
  recommendedTierId?: string;
  recommendedPriceId?: string;
  isGiftingEnabled: boolean;
  isGiftingOnly: boolean;
  nodeAttributes: TPricingAttributes;
}

const toStr = (v: unknown): string => (typeof v === 'string' ? v : '');

export const UpgradeProvider: React.FC<UpgradeProviderProps> = ({
  children,
  tiers,
  giftTiers,
  groupTiers,
  isInternalCheckout,
  recommendedTierId,
  recommendedPriceId,
  isGiftingEnabled,
  isGiftingOnly,
  nodeAttributes,
}) => {
  const { site, isLoggedIn } = useRouteLoaderData<typeof loader>('root');
  const publication = site.publication;

  // Only load Stripe if there are non-free tiers. Include gift/group tiers so a
  // visitor whose default individual selection is free can still switch into the
  // (always paid) gift or group flow without landing on an empty Stripe session.
  const shouldLoadStripe = useMemo(
    () => [...tiers, ...giftTiers, ...groupTiers].some((tier) => tier.id !== 'free'),
    [tiers, giftTiers, groupTiers],
  );
  const { stripeSession, isLoadingStripeSession } = useStripeSession({
    publication,
    shouldLoadStripe,
  });

  const [email, setEmail] = useEmail();
  const [taxId, setTaxId] = useState('');
  const [donationAmountCents, setDonationAmountCents] = useState(0);
  const { selectedTier, setSelectedTier, selectedPrice, setSelectedPrice } = useResolvedTierSelection(
    tiers,
    recommendedTierId,
    recommendedPriceId,
  );
  const {
    selectedTier: selectedGiftTier,
    setSelectedTier: setSelectedGiftTier,
    selectedPrice: selectedGiftPrice,
    setSelectedPrice: setSelectedGiftPrice,
  } = useResolvedTierSelection(giftTiers, recommendedTierId, recommendedPriceId);
  const {
    selectedTier: selectedGroupTier,
    setSelectedTier: setSelectedGroupTier,
    selectedPrice: selectedGroupPrice,
    setSelectedPrice: setSelectedGroupPrice,
  } = useResolvedTierSelection(groupTiers, recommendedTierId, recommendedPriceId);
  const [seatCount, setSeatCount] = useState<number | null>(null);
  const [groupName, setGroupName] = useState('');
  const [currentFormMode, setCurrentFormMode] = useState<PricingFormMode>(
    isGiftingEnabled && isGiftingOnly ? 'gift' : 'individual',
  );

  const isStripeLoading = isLoadingStripeSession;

  const appearanceRules = useMemo((): Record<string, Record<string, string>> => {
    const n = nodeAttributes;
    return {
      '.Block': {
        backgroundColor: toStr(n.inputBackgroundColor),
        borderStyle: toStr(n.inputBorderStyle),
        borderWidth: toStr(n.inputBorderWidth),
        borderColor: toStr(n.inputBorderColor),
        borderRadius: toStr(n.inputBorderRadius),
        boxShadow: 'none',
      },
      '.RedirectText': {
        color: toStr(n.inputTextColor),
      },
      '.Tab': {
        backgroundColor: toStr(n.cardBackgroundColor),
        color: toStr(n.nameColor),
        padding: '8px',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: toStr(n.cardBorderColor),
      },
      '.Tab:hover': {
        backgroundColor: toStr(n.cardBackgroundColor),
        color: toStr(n.nameColor),
      },
      '.TabIcon': {
        fill: toStr(n.nameColor),
      },
      '.Tab--selected': {
        backgroundColor: toStr(n.cardSelectedBackgroundColor),
        color: toStr(n.nameSelectedColor),
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: toStr(n.cardSelectedBorderColor),
      },
      '.Tab--selected:hover': {
        backgroundColor: toStr(n.cardSelectedBackgroundColor),
        color: toStr(n.nameSelectedColor),
      },
      '.TabIcon--selected': {
        fill: toStr(n.nameSelectedColor),
      },
      '.Label': {
        color: toStr(n.inputPlaceholderColor),
      },
      '.Label--resting': {
        fontSize: '14px',
      },
      '.Input': {
        padding: '12px 16px',
        color: toStr(n.inputTextColor),
        backgroundColor: toStr(n.inputBackgroundColor),
        fontSize: '14px',
        borderStyle: toStr(n.inputBorderStyle),
        borderWidth: toStr(n.inputBorderWidth),
        borderColor: toStr(n.inputBorderColor),
        borderRadius: toStr(n.inputBorderRadius),
      },
      '.Input::placeholder': {
        color: toStr(n.inputPlaceholderColor),
        fontSize: '14px',
      },
      '.Input--invalid': {
        outline: '0',
        borderStyle: toStr(n.inputBorderStyle),
        borderWidth: toStr(n.inputBorderWidth),
        borderColor: toStr(n.inputBorderColor),
        borderRadius: toStr(n.inputBorderRadius),
        boxShadow: 'none',
      },
      '.Error': {
        color: toStr(n.inputTextColor),
        backgroundColor: toStr(n.inputBackgroundColor),
        padding: '12px 16px',
        borderRadius: '6px',
        fontSize: '12px',
      },
    };
  }, [nodeAttributes]);

  const stripeElementsOptions: StripeElementsOptions | undefined = useMemo(() => {
    if (!isInternalCheckout) return undefined;

    const priceByFormMode: Record<PricingFormMode, Price | undefined> = {
      individual: selectedPrice,
      gift: selectedGiftPrice,
      group: selectedGroupPrice,
    };
    const price = priceByFormMode[currentFormMode];
    if (!price) return undefined;

    const baseOptions: StripeElementsOptions = {
      // If we wanted to manually specify the payment methods to display
      // we would use paymentMethodTypes: ['card', 'apple_pay', 'google_pay', '...']
      // The same happens in the backend when creating a setup intent in
      // Stripe::OrchidUpgrades::CreateSubscription#payment_settings
      paymentMethodTypes: ['card'],
      currency: price.currency,
      amount: price.amount_cents,
      locale: publication?.language,
      appearance: {
        theme: 'flat' as const,
        labels: 'floating' as const,
        variables: {
          borderRadius: (nodeAttributes.inputBorderRadius ?? PRICING_ELEMENT_DEFAULT_INPUT_BORDER_RADIUS) as string,
          focusOutline: 'none',
          focusBoxShadow: 'none',
        },
        inputs: 'condensed' as const,
        rules: appearanceRules,
      },
      mode: 'payment',
      paymentMethodCreation: 'manual',
    };

    if (price.interval === PriceInterval.DONATION) {
      if (donationAmountCents > 0) {
        return {
          ...baseOptions,
          amount: donationAmountCents,
        };
      }

      return {
        ...baseOptions,
        amount: 100, // minimum amount for payment elements
      };
    }

    if (currentFormMode === 'group' && selectedGroupPrice && selectedGroupTier) {
      // Stripe Elements rejects amount=0, so fall back to the tier minimum (or the global
      // default) when the user hasn't typed a seat count yet. The form summary still gates
      // on the real seatCount; this only affects the Elements `amount` option.
      const minimumQuantityForFallback = selectedGroupTier.group_minimum_quantity ?? DEFAULT_GROUP_MIN_QUANTITY;
      const elementsQuantity = seatCount && seatCount > 0 ? seatCount : minimumQuantityForFallback;
      return {
        ...baseOptions,
        amount: computeGroupTotalCents({
          amountCents: selectedGroupPrice.original_amount_cents,
          quantity: elementsQuantity,
          discountPercent: selectedGroupTier.group_discount_percent,
        }),
      };
    }

    return baseOptions;
  }, [
    selectedPrice,
    selectedGiftPrice,
    selectedGroupPrice,
    selectedGroupTier,
    seatCount,
    donationAmountCents,
    currentFormMode,
    publication?.language,
    appearanceRules,
    isInternalCheckout,
    nodeAttributes.inputBorderRadius,
  ]);

  const canLoginWithSSO = useMemo(() => {
    if (publication?.has_subscription_password) return false;
    if (isLoggedIn) return false;
    if (currentFormMode === 'gift' || currentFormMode === 'group') return false;
    if (!isInternalCheckout) return false;
    return true;
  }, [isInternalCheckout, isLoggedIn, currentFormMode, publication]);

  return (
    <UpgradeContext.Provider
      value={{
        email,
        setEmail,
        showTaxIdField: isInternalCheckout ? !!publication?.subscriber_tax_id_collection_enabled : false,
        taxId,
        setTaxId,
        donationAmountCents,
        setDonationAmountCents,
        selectedTier,
        setSelectedTier,
        selectedPrice,
        setSelectedPrice,
        selectedGiftTier,
        setSelectedGiftTier,
        selectedGiftPrice,
        setSelectedGiftPrice,
        selectedGroupTier,
        setSelectedGroupTier,
        selectedGroupPrice,
        setSelectedGroupPrice,
        seatCount,
        setSeatCount,
        groupName,
        setGroupName,
        isStripeLoading,
        stripeSession,
        isInternalCheckout,
        currentFormMode,
        setCurrentFormMode,
        stripeElementsOptions,
        canLoginWithSSO,
      }}
    >
      {children}
    </UpgradeContext.Provider>
  );
};
